import { getObservabilityContext } from './context';
import { normalizePath } from './privacy';
import { OBSERVABILITY_RUNTIME } from './runtime';
import { emitTelemetry } from './telemetry';

export const CHUNK_RECOVERY_COOLDOWN_MS = 5 * 60 * 1000;
export const CHUNK_RECOVERY_STORAGE_KEY = 'aqt:chunk-load-recovery:v1';

type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem'>;

type ChunkFailureKind = 'css-chunk' | 'dynamic-import' | 'webpack-chunk';

export type ChunkLoadFailure = Readonly<{
  assetPath: string;
  kind: ChunkFailureKind;
}>;

export type ChunkLoadRecovery = Readonly<{
  action: 'reload' | 'show-refresh' | 'wait-for-reload';
  failure: ChunkLoadFailure;
  reason: 'cooldown' | 'first-failure' | 'same-page-reload-pending' | 'storage-unavailable';
}>;

export type ChunkLoadRecoveryOptions = Readonly<{
  now?: number;
  pageLoadId?: string;
  storage?: RecoveryStorage | null;
}>;

type RecoveryMarker = Readonly<{
  pageLoadId: string;
  timestamp: number;
}>;

const PAGE_LOAD_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const ASSET_URL_PATTERN = /(?:https?:\/\/[^\s)'"`]+|\/?assets\/[^\s)'"`]+?\.(?:css|js)(?:[?#][^\s)'"`]*)?)/i;

function errorParts(error: unknown): { message: string; name: string } {
  if (error instanceof Error) return { message: error.message, name: error.name };
  if (error && typeof error === 'object') {
    const value = error as { message?: unknown; name?: unknown };
    return {
      message: typeof value.message === 'string' ? value.message : String(error),
      name: typeof value.name === 'string' ? value.name : '',
    };
  }
  return { message: String(error), name: '' };
}

function assetPath(message: string): string {
  const match = message.match(ASSET_URL_PATTERN)?.[0];
  if (!match) return 'unknown';
  try {
    const pathname = new URL(match, globalThis.location?.origin ?? 'https://app.alphietrader.com').pathname;
    return pathname.startsWith('/') ? pathname : `/${pathname}`;
  } catch {
    return 'unknown';
  }
}

export function classifyChunkLoadFailure(error: unknown): ChunkLoadFailure | undefined {
  const { message, name } = errorParts(error);
  const combined = `${name}: ${message}`;
  let kind: ChunkFailureKind | undefined;

  if (/Loading CSS chunk\s+.+?\s+failed/i.test(combined)) kind = 'css-chunk';
  else if (/Loading chunk\s+.+?\s+failed/i.test(combined) || /ChunkLoadError/i.test(name)) kind = 'webpack-chunk';
  else if (
    /Failed to fetch dynamically imported module/i.test(combined) ||
    /error loading dynamically imported module/i.test(combined) ||
    /Importing a module script failed/i.test(combined) ||
    /Failed to load module script/i.test(combined)
  ) {
    kind = 'dynamic-import';
  }

  return kind ? { assetPath: assetPath(message), kind } : undefined;
}

function browserSessionStorage(): RecoveryStorage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function parseMarker(value: string | null): RecoveryMarker | undefined {
  if (!value) return undefined;
  try {
    const marker = JSON.parse(value) as Partial<RecoveryMarker>;
    if (typeof marker.pageLoadId !== 'string' || !marker.pageLoadId || !Number.isFinite(marker.timestamp)) return undefined;
    return { pageLoadId: marker.pageLoadId, timestamp: marker.timestamp as number };
  } catch {
    return undefined;
  }
}

export function prepareChunkLoadRecovery(
  error: unknown,
  { now = Date.now(), pageLoadId = PAGE_LOAD_ID, storage: suppliedStorage }: ChunkLoadRecoveryOptions = {}
): ChunkLoadRecovery | undefined {
  const failure = classifyChunkLoadFailure(error);
  if (!failure) return undefined;

  const storage = suppliedStorage === undefined ? browserSessionStorage() : suppliedStorage;
  if (!storage) return { action: 'show-refresh', failure, reason: 'storage-unavailable' };

  let marker: RecoveryMarker | undefined;
  try {
    marker = parseMarker(storage.getItem(CHUNK_RECOVERY_STORAGE_KEY));
  } catch {
    return { action: 'show-refresh', failure, reason: 'storage-unavailable' };
  }

  if (marker && now - marker.timestamp < CHUNK_RECOVERY_COOLDOWN_MS) {
    return marker.pageLoadId === pageLoadId
      ? { action: 'wait-for-reload', failure, reason: 'same-page-reload-pending' }
      : { action: 'show-refresh', failure, reason: 'cooldown' };
  }

  try {
    storage.setItem(CHUNK_RECOVERY_STORAGE_KEY, JSON.stringify({ pageLoadId, timestamp: now } satisfies RecoveryMarker));
  } catch {
    return { action: 'show-refresh', failure, reason: 'storage-unavailable' };
  }

  return { action: 'reload', failure, reason: 'first-failure' };
}

export function reportChunkLoadRecovery(recovery: ChunkLoadRecovery, source: string) {
  const context = getObservabilityContext();
  return emitTelemetry(
    'frontend.chunk-load-recovery',
    {
      action: recovery.action,
      assetPath: recovery.failure.assetPath,
      failureKind: recovery.failure.kind,
      reason: recovery.reason,
      route: normalizePath(context.route),
      source,
      version: OBSERVABILITY_RUNTIME.version,
    },
    context
  );
}
